/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PhysicianProps {
  id: string;
  firstName: string;
  lastName: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PhysicianRecord extends Record<PhysicianProps>({
  id: null as any,
  firstName: null as any,
  lastName: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    firstName: string;
    lastName: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PhysicianRecord {
    if (json == null) {
      throw new Error('PhysicianRecord.fromJS: parameter cannot be null');
    }
    return new PhysicianRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PhysicianJson.id',
      ),
      firstName: getFromOrThrow(json, (j) => j.firstName, 'PhysicianJson.firstName'),
      lastName: getFromOrThrow(json, (j) => j.lastName, 'PhysicianJson.lastName'),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    firstName: string;
    lastName: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PhysicianRecord, Error[]] {
    if (json == null) {
      throw new Error('PhysicianRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PhysicianRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PhysicianJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.firstName, 'PhysicianJson.firstName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.lastName, 'PhysicianJson.lastName'),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PhysicianProps) {
    super(props);
    if (props == null) {
      throw new Error('PhysicianRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PhysicianViewModelBase implements PhysicianProps {
  protected readonly _props: PhysicianProps;
  private _json?: PhysicianProps;

  get id(): string {
    return this._props.id;
  }
  get firstName(): string {
    return this._props.firstName;
  }
  get lastName(): string {
    return this._props.lastName;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: PhysicianProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        firstName: this._props.firstName,
        lastName: this._props.lastName,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPhysicianEntities = (state: AppState) =>
  state.entityStores.physician.entities;

export const selectPhysicianById = (id: string) => (state: AppState) =>
  state.entityStores.physician.entities.get(id);

export const selectPhysicianMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.physician.meta.get(id);

export const selectPhysicianAndMetadataById = (id: string) =>
  createSelector(selectPhysicianById(id), selectPhysicianMetadataById(id), (physician, meta) => ({
    physician,
    meta,
  }));

export function selectPhysicianEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPhysicianEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PhysicianRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPhysicianEntitiesByFirstName(firstName: string) {
  return createSelector(selectAllPhysicianEntities, (entities) =>
    entities.filter((e) => e.firstName === firstName).valueSeq(),
  );
}

export function selectPhysicianEntitiesByLastName(lastName: string) {
  return createSelector(selectAllPhysicianEntities, (entities) =>
    entities.filter((e) => e.lastName === lastName).valueSeq(),
  );
}
//#endregion Selectors
